import { CacheDataModel } from '../../../cmg-1003/models/auto-recovery.model';
import { LoanStatus, LockStatusType } from '../../../cmg-1003/models/cmg-1003-common.enum';
import { FeaturePermissions } from '../../../cmg-1003/models/feature-permissions.model';
import { Cmg1003SectionTypes, CreditSectionTypes } from '../../../cmg-1003/models/section-types.enum';
import { ValidationFailureModel } from '../../../cmg-1003/models/validation-panel.model';
import { UserSession } from '../../../disclosure/models/user-session.model';
import { DesktopUnderwritingSystemTypes } from '../../../aus/models';
import { LoanSummarySectionTypes } from '../../../loan-summary/models/loan-summary-section-types.enum';
import { MsaCounties } from '../../../cmg-1003/models';
import { Models as GlobalModels } from '../../../models/global.models';
import { ToastMessageOptions } from 'primeng/api';
import { Models } from '../../../pipeline/models/global.models';

type abbreviatedState = string;

export enum IntegratedLoginContext {
  Byte = 'byte',
}

export interface SessionModel {
  token: string | null;
  name: string;
  role: string;
  loanStatusId?: LoanStatus;
  rateLockStatus?: LockStatusType;
  is1003Invalid: boolean;
  isRespaComplete: boolean;
  validationErrors: ValidationFailureModel[];
  validationWarnings: ValidationFailureModel[];
  scrollAdjustNavItem?: Cmg1003SectionTypes | CreditSectionTypes | LoanSummarySectionTypes;
  autoScrolling: boolean;
  all1003Loaded: boolean;
  shouldReload1003: boolean;
  pauseUpdateActiveTab: boolean;
  lastUpdatedDateTime: number;
  lastRefreshDateTime: Date;
  revalidateValidationTrigger: Date;
  hasAusPanelBeenOpened: boolean;
  integratedLoginContext?: IntegratedLoginContext;
  integratedTargetUrl?: string;
  integratedUserOrgProfileId?: number;
  integratedSessionActive?: boolean;

  msaCounties: Record<abbreviatedState, MsaCounties[]>;

  // Used to hide validation when *either* borrower selects 'Yes' for the question
  // about serving in Armed Forces. Initially this validation will show for both
  // borrowers.
  // Value will be borrower id of first borrower for which 'Yes' is selected to question about serving.
  borrowerHasServedInArmedForces?: number;

  /**
   * Caution: Do not overwrite cmgRetailAccessToken and featurePermissions
   * They only get set once from Clear 1(or from mock in dev environment)
   */
  cmgRetailAccessToken: any;

  featurePermissions: FeaturePermissions;

  /**
   * userId of the CMG User logged into Clear 1.
   */
  userId: number;

  /**
   * userName of the CMG User logged into Clear 1.
   */
  userName: string;

  firstName: string;

  lastName: string;

  email?: string;

  // ======== START: Auto-Recovery ======== //
  lastAutoRecoverySaveTime?: number;

  /** Whether we need the user to decide on selecting between auto-recovered vs. Byte version of 1003 file. */
  needsAutoRecoveryDecision?: boolean;

  /** The cached version of this 1003 file, stored in Redis by the auto-recovery pipeline. */
  cached1003?: CacheDataModel;

  /** Represents when the last save occurred for this 1003 file. */
  lastSavedDateTime?: Date;

  /** The number of failed attempts to invoke Auto-Recovery PUT request. */
  failedAutoRecoveryAttempts?: number;

  // ======== END: Auto-Recovery ======== //

  /**
   * fileDataId is added here to make sure even if the get call fails
   * we have requested fileDataId
   */
  fileDataId: number;

  dislosureSession: UserSession;

  /**
   * Validation in AUS needs to be triggerred outside of regular validation approach of 1003 sections for 2 reasons
   * 1) aus does not fall into the same component tree as in 1003 components
   * 2) aus validations can also be triggered from pipeline where there are no 1003 components(like borrower, etc.)
   * A note, as Clear 2 project has grown bigger in scope, the initial approach of triggering validations by bubbling it down from
   * 1003 down to components no longer seems sufficient. A better approach would be to trigger it from state variables and
   * once the component has finished validating they can set the validation to done. This however requires a lot of refactoring :-(.
   *
   * validateAusSection: set to true when aus section need to be validated. Once Aus is done validating it can just set it to false.
   * Naming the property to validateAusSection so as not to confuse it with bigger AUS Validation - this is only the AUS section validation.
   */
  validateAusSection: boolean;

  /**
   * Message Service does not work from Loan Save HandleError(since it throws an error and call stack is lost.).
   * This is a work around. Before going with this approach I have tried following:
   *  - setTimeout did not work.
   *  - asyncScheduler - did not work
   *  - runOutsideAngular - did not work
   * URLA2-1550
   */
  cmg1003Message: ToastMessageOptions;

  /**
   * https://dev.azure.com/cmgfidev/CLEAR%202/_workitems/edit/5652
   * currently only used for picking whether to show LPA Validation or not.
   */
  selectedAusType?: DesktopUnderwritingSystemTypes;

  clear2UIAppVersion?: string;

  /** User Settings */
  userSettings?: GlobalModels.UserSettings;

  /**
   * Checker when user will open sandbox loan
   * that can be performed only from the original loan
   * direct access with url is not allowed
   */
  isValidAccessSandboxLoan?: boolean;

  previousSandboxLoanFileId?: number;

  isCA885FormInvalid?: boolean;

  nmlsid?: string;

  partnersAccess: Models.PartnersAccessModel;
}

export function createInitialSessionState(): SessionModel {
  return <SessionModel>{
    token: null,
    name: null,
    role: null,
    loanStatusId: null,
    is1003Invalid: false,
    validationErrors: [],
    validationWarnings: [],
    scrollAdjustNavItem:
      // eslint-disable-next-line no-bitwise
      Cmg1003SectionTypes.borrowerInformation | CreditSectionTypes.requestCreditReport | LoanSummarySectionTypes.borrowerInformation,
    autoScrolling: false,
    all1003Loaded: false,
    cmgRetailAccessToken: null,
    featurePermissions: null,
    lastUpdatedDateTime: null,
    hasAusPanelBeenOpened: false,
    lastAutoRecoverySaveTime: null,
    fileDataId: null,
    dislosureSession: null,
    validateAusSection: false,
    selectedAusType: null, // initial value
  };
}
